import jsonpatch from "fast-json-patch";
import type { Operation } from "fast-json-patch";
import { jsonEqual, toJsonDocument } from "./jsonDocument.ts";

const { applyPatch, compare } = jsonpatch;

export type EditorCommitOptions = {
  coalesceKey?: string;
};

export type HistoryEntry = {
  forward: Operation[];
  inverse: Operation[];
};

export type EditorHistoryOptions = {
  maxEntries?: number;
  maxBytes?: number;
  coalesceMs?: number;
  now?: () => number;
};

const DEFAULT_MAX_ENTRIES = 50;
const DEFAULT_MAX_BYTES = 1_000_000;
const DEFAULT_COALESCE_MS = 500;

const patchBytes = (entries: HistoryEntry[]) =>
  entries.reduce(
    (sum, entry) =>
      sum +
      JSON.stringify(entry.forward).length +
      JSON.stringify(entry.inverse).length,
    0,
  );

const applyInverse = <T extends object>(document: T, patch: Operation[]): T => {
  const result = applyPatch(
    toJsonDocument(document),
    toJsonDocument(patch),
    false,
    false,
  );
  return toJsonDocument(result.newDocument);
};

export class EditorHistoryEngine<T extends object> {
  present: T;
  private lastCommitted: T;
  private savedBaseline: T;
  private past: HistoryEntry[] = [];
  private future: HistoryEntry[] = [];
  private coalesce: {
    key: string;
    startedAt: number;
    frozenBaseline: T;
  } | null = null;
  private readonly maxEntries: number;
  private readonly maxBytes: number;
  private readonly coalesceMs: number;
  private readonly now: () => number;

  constructor(initial: T, options: EditorHistoryOptions = {}) {
    const normalized = toJsonDocument(initial);
    this.present = normalized;
    this.lastCommitted = toJsonDocument(normalized);
    this.savedBaseline = toJsonDocument(normalized);
    this.maxEntries = options.maxEntries ?? DEFAULT_MAX_ENTRIES;
    this.maxBytes = options.maxBytes ?? DEFAULT_MAX_BYTES;
    this.coalesceMs = options.coalesceMs ?? DEFAULT_COALESCE_MS;
    this.now = options.now ?? Date.now;
  }

  get canUndo() {
    return this.past.length > 0;
  }

  get canRedo() {
    return this.future.length > 0;
  }

  get hasChanges() {
    return !jsonEqual(this.present, this.savedBaseline);
  }

  reset(next: T, options?: { markDirty?: boolean }) {
    const normalized = toJsonDocument(next);
    this.present = normalized;
    this.lastCommitted = toJsonDocument(normalized);
    if (!options?.markDirty) {
      this.savedBaseline = toJsonDocument(normalized);
    }
    this.past = [];
    this.future = [];
    this.coalesce = null;
    return this.present;
  }

  rebaseline(next: T = this.present) {
    this.savedBaseline = toJsonDocument(next);
    return this.hasChanges;
  }

  replacePresent(next: T) {
    const normalized = toJsonDocument(next);
    this.present = normalized;
    this.lastCommitted = toJsonDocument(normalized);
    this.coalesce = null;
    return this.present;
  }

  flushCoalesce() {
    if (!this.coalesce) return;
    this.coalesce = null;
    this.lastCommitted = toJsonDocument(this.present);
  }

  commit(nextRaw: T, options: EditorCommitOptions = {}) {
    const next = toJsonDocument(nextRaw);
    const coalesceKey = options.coalesceKey;
    const now = this.now();
    const inWindow = Boolean(
      coalesceKey &&
        this.coalesce &&
        this.coalesce.key === coalesceKey &&
        now - this.coalesce.startedAt < this.coalesceMs,
    );

    if (inWindow && this.coalesce) {
      const frozen = this.coalesce.frozenBaseline;
      const forward = compare(frozen, next);
      const inverse = compare(next, frozen);
      if (forward.length === 0) {
        this.present = next;
        return this.present;
      }
      this.past[this.past.length - 1] = { forward, inverse };
      this.present = next;
      this.future = [];
      this.trim();
      return this.present;
    }

    this.flushCoalesce();

    const forward = compare(this.lastCommitted, next);
    const inverse = compare(next, this.lastCommitted);
    if (forward.length === 0) {
      this.present = next;
      this.lastCommitted = toJsonDocument(next);
      return this.present;
    }

    this.past.push({ forward, inverse });
    this.future = [];
    this.present = next;
    if (coalesceKey) {
      this.coalesce = {
        key: coalesceKey,
        startedAt: now,
        frozenBaseline: toJsonDocument(this.lastCommitted),
      };
    } else {
      this.lastCommitted = toJsonDocument(next);
    }
    this.trim();
    return this.present;
  }

  undo() {
    this.coalesce = null;
    const entry = this.past.pop();
    if (!entry) return null;
    this.present = applyInverse(this.present, entry.inverse);
    this.lastCommitted = toJsonDocument(this.present);
    this.future.push(entry);
    return this.present;
  }

  redo() {
    this.coalesce = null;
    const entry = this.future.pop();
    if (!entry) return null;
    this.present = applyInverse(this.present, entry.forward);
    this.lastCommitted = toJsonDocument(this.present);
    this.past.push(entry);
    return this.present;
  }

  private trim() {
    while (this.past.length > this.maxEntries) {
      this.past.shift();
    }
    while (
      this.past.length > 1 &&
      patchBytes(this.past) + patchBytes(this.future) > this.maxBytes
    ) {
      this.past.shift();
    }
  }
}
